import { useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import type { BarCustomLayerProps } from '@nivo/bar';
import {
  buildGtpChartRows,
  type GeneGtpItem,
  type GtpChartRow,
} from '@/utils/formatGtpList';

const CHART_THEME = {
  axis: {
    ticks: { text: { fontSize: 10, fill: '#64748b' } },
    legend: { text: { fontSize: 11, fill: '#475569', fontWeight: 500 } },
  },
  grid: { line: { stroke: '#eef2f7', strokeWidth: 1 } },
};

type GtpChartProps = {
  items: GeneGtpItem[];
};

function RowLabels({
  bars,
  labelById,
  fullNameById,
}: Pick<BarCustomLayerProps<GtpChartRow>, 'bars'> & {
  labelById: Record<string, string>;
  fullNameById: Record<string, string>;
}) {
  return (
    <g pointerEvents="none">
      {bars.map(bar => {
        const id = String(bar.data.indexValue);
        const label = labelById[id] ?? id;
        const y = bar.y + bar.height / 2;

        return (
          <text
            key={bar.key}
            x={-8}
            y={y}
            textAnchor="end"
            dominantBaseline="central"
            style={{ fontSize: 10, fill: '#374151', fontWeight: 500 }}
          >
            <title>{fullNameById[id] ?? label}</title>
            {label}
          </text>
        );
      })}
    </g>
  );
}

export function GtpChart({ items }: GtpChartProps) {
  const chartData = useMemo(() => buildGtpChartRows(items), [items]);

  const labelById = useMemo(
    () => Object.fromEntries(chartData.map(row => [row.id, row.label])),
    [chartData],
  );

  const fullNameById = useMemo(
    () => Object.fromEntries(chartData.map(row => [row.id, row.fullName])),
    [chartData],
  );

  const tcesExtent = useMemo(() => {
    const absMax = Math.max(...chartData.map(row => Math.abs(row.tces)), 1);
    const pad = absMax * 0.12;
    return { min: -(absMax + pad), max: absMax + pad };
  }, [chartData]);

  const leftMargin = useMemo(() => {
    const longest = Math.max(...chartData.map(row => row.label.length), 12);
    return Math.min(220, Math.max(148, longest * 6.1 + 16));
  }, [chartData]);

  if (!chartData.length) {
    return <p className="px-3 py-6 text-center text-sm text-gray-400">No GTP data available.</p>;
  }

  return (
    <ResponsiveBar
      data={chartData}
      keys={['tces']}
      indexBy="id"
      layout="horizontal"
      margin={{ top: 8, right: 12, bottom: 40, left: leftMargin }}
      padding={0.28}
      valueScale={{ type: 'linear', min: tcesExtent.min, max: tcesExtent.max }}
      colors={bar => String(bar.data.color)}
      theme={CHART_THEME}
      enableGridX
      enableGridY={false}
      markers={[
        {
          axis: 'x',
          value: 0,
          lineStyle: { stroke: '#94a3b8', strokeWidth: 1 },
        },
      ]}
      axisBottom={{
        legend: 'TCES',
        legendOffset: 32,
        legendPosition: 'middle',
        tickSize: 0,
        tickPadding: 6,
      }}
      axisLeft={{
        tickSize: 0,
        renderTick: () => <g />,
      }}
      enableLabel={false}
      borderRadius={1}
      layers={[
        'grid',
        'axes',
        'bars',
        'markers',
        props => <RowLabels {...props} labelById={labelById} fullNameById={fullNameById} />,
      ]}
      tooltip={({ indexValue, value, data: barData }) => (
        <div className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs shadow-md">
          <strong className="block text-slate-800">{String(barData.fullName ?? indexValue)}</strong>
          <span className="text-slate-500">TCES: {Number(value).toFixed(2)}</span>
        </div>
      )}
    />
  );
}
